import { CombinedFlows, TransactionTypes } from './flow-types';

export function buildParticipantList(dataElement, identity, t) {
  if (!dataElement?.to) {
    return [{
      id: "MY_PITSTOP",
      name: t("myPitstop"),
      iconUrl: identity?.organization?.icon_url || "",
      on_behalf_of: []
    }];
  }

  // Group participants by ID to detect duplicates
  const participantMap = new Map();
  
  dataElement.to.forEach(element => {
    const participantId = element.id;
    
    if (!participantMap.has(participantId)) {
      participantMap.set(participantId, {
        base: element,
        hasPOBOP: false,
        hasPOB: false,
        hasContributor: false,
        pobopEntry: null,
        pobEntry: null,
        contributorEntry: null
      });
    }
    
    const entry = participantMap.get(participantId);
    
    // Check if this is a POBOP entry
    if (element.for_principals && element.for_principals.length > 0) {
      entry.hasPOBOP = true;
      entry.pobopEntry = element;
    }
    
    // Check if this is a POB entry (has other orgs in on_behalf_of)
    if (element.on_behalf_of && element.on_behalf_of.length > 0) {
      const hasOtherOrgs = element.on_behalf_of.some(
        org => org.id !== identity?.organization?.id
      );
      const hasCurrentOrg = element.on_behalf_of.some(
        org => org.id === identity?.organization?.id
      );
      
      if (hasOtherOrgs && !element.for_principals) {
        entry.hasPOB = true;
        entry.pobEntry = element;
      }
      
      // Direct contributor relationship (current org in on_behalf_of, no for_principals)
      if (hasCurrentOrg && !element.for_principals) {
        entry.hasContributor = true;
        entry.contributorEntry = element;
      }
    }
  });
  
  // Build participant list
  const participantList = [{
    id: "MY_PITSTOP",
    name: t("myPitstop"),
    iconUrl: identity?.organization?.icon_url || "",
    on_behalf_of: []
  }];
  
  participantMap.forEach((entry, participantId) => {
    const { base, hasPOBOP, hasPOB, hasContributor, pobopEntry, pobEntry, contributorEntry } = entry;
    
    let mergedType = null;
    let on_behalf_of_list = [];
    
    if (hasPOBOP && hasPOB && hasContributor) {
      mergedType = CombinedFlows.CONTRIBUTOR_POB_POBOP;
      on_behalf_of_list = contributorEntry?.on_behalf_of || pobEntry?.on_behalf_of || [];
    } else if (hasPOBOP && hasPOB && !hasContributor) {
      mergedType = CombinedFlows.POB_POBOP;
      on_behalf_of_list = pobEntry?.on_behalf_of || [];
    } else if (hasPOBOP && hasContributor && !hasPOB) {
      mergedType = CombinedFlows.CONTRIBUTOR_POBOP;
      on_behalf_of_list = contributorEntry?.on_behalf_of || [];
    }
    
    // If any merged type, create merged participant
    if (mergedType) {
      participantList.push({
        ...base,
        id: participantId,
        isMerged: true,
        mergedType: mergedType,
        hasPOBOP: hasPOBOP,
        hasPOB: hasPOB,
        hasContributor: hasContributor,
        for_principals: pobopEntry?.for_principals || [],
        on_behalf_of: on_behalf_of_list,
        systems: base.systems
      });
    }
    // Only POBOP
    else if (hasPOBOP) {
      participantList.push({
        ...pobopEntry,
        id: `${participantId}_POBOP`,
        originalId: participantId,
        isPOBOP: true,
        for_principals: pobopEntry.for_principals || []
      });
    }
    // Only POB
    else if (hasPOB) {
      participantList.push({
        ...pobEntry,
        id: participantId,
        on_behalf_of: pobEntry.on_behalf_of || []
      });
    }
    // Only Contributor
    else if (hasContributor) {
      participantList.push({
        ...contributorEntry,
        id: participantId,
        on_behalf_of: contributorEntry.on_behalf_of || []
      });
    }
    // Regular participant
    else {
      participantList.push({
        ...base,
        id: participantId
      });
    }
  });

  return participantList;
}

export function addAgentParticipants(participantList, dataElement) {
  const agentParticipants = dataElement?.to_agents
    ?.map(element => ({ 
      ...element, 
      id: `${element.id}_AGENT`,
      originalId: element.id,
      isAgent: true,      
      to_principals: element.to_principals || [],
      on_behalf_of: element.on_behalf_of || []
    })) || [];

  return [...participantList, ...agentParticipants];
}

function isBehalfPanelValid(behalfPanel, onBehalf) {
  return !behalfPanel || onBehalf.length > 0;
}

function validatePushToAgent(recipients, onBehalf, selectedPrincipals, behalfPanel) {
  const isValidAgent = Boolean(recipients[0]?.isAgent);
  const hasPrincipals = selectedPrincipals.length > 0;
  const isBehalfValid = isBehalfPanelValid(behalfPanel, onBehalf);
  return isValidAgent && hasPrincipals && isBehalfValid;
}

function validateMergedParticipant(recipients, onBehalf, selectedPrincipals, transactionType) {
  const mergedType = recipients[0]?.mergedType;
  
  if (shouldApplyOrLogic(recipients, transactionType)) {
    return onBehalf.length > 0 || selectedPrincipals.length > 0;
  }

  if (mergedType === CombinedFlows.CONTRIBUTOR_POBOP) {
    return true;
  }

  return onBehalf.length > 0 || selectedPrincipals.length > 0;
}

export function validateParticipantSelection(
  isAgentPrincipalUiEnabled,
  recipients,
  onBehalf,
  selectedPrincipals,
  behalfPanel,
  transactionType
) {
  if (!isAgentPrincipalUiEnabled) {
    return isBehalfPanelValid(behalfPanel, onBehalf);
  }

  if (transactionType === TransactionTypes.PUSH_TO_AGENT) {
    return validatePushToAgent(recipients, onBehalf, selectedPrincipals, behalfPanel);
  }

  const isMergedParticipant = recipients[0]?.isMerged;
  
  if (isMergedParticipant) {
    return validateMergedParticipant(recipients, onBehalf, selectedPrincipals, transactionType);
  }
  
  return isBehalfPanelValid(behalfPanel, onBehalf);
}

export function handleRecipientChangeLogic(isAgentPrincipalUiEnabled, option, currentRecipient) {
  if (!isAgentPrincipalUiEnabled) {
    return {
      principalsList: [],
      selectedPrincipals: []
    };
  }

  // Handle merged participants (POBOP + POB)
  if (option?.[0]?.isMerged) {
    const myPrincipalsList = option[0].for_principals.map(principal => ({
      id: principal.uen || principal.name,
      name: principal.name,
      uen: principal.uen,
    }));

    return {
      principalsList: myPrincipalsList,
      selectedPrincipals: []
    };
  }
  // Handle Agent recipients
  else if (option?.[0]?.isAgent && option[0]?.to_principals) {
    const newPrincipalsList = option[0].to_principals.map(principal => ({
      id: principal.uen || principal.name,
      name: principal.name,
      uen: principal.uen,
      systemIds: principal.systemIds,
    }));

    // Only clear selectedPrincipals if switching to a different agent
    const shouldClearSelected = !currentRecipient || currentRecipient[0]?.id !== option[0]?.id;

    return {
      principalsList: newPrincipalsList,
      selectedPrincipals: shouldClearSelected ? [] : undefined // undefined means don't update
    };
  }
  // Handle POBOP recipients
  else if (option?.[0]?.isPOBOP && option[0]?.for_principals) {
    const myPrincipalsList = option[0].for_principals.map(principal => ({
      id: principal.uen || principal.name,
      name: principal.name,
      uen: principal.uen,
    }));

    // Only clear selectedPrincipals if switching to a different POBOP
    const shouldClearSelected = !currentRecipient || currentRecipient[0]?.id !== option[0]?.id;

    return {
      principalsList: myPrincipalsList,
      selectedPrincipals: shouldClearSelected ? [] : undefined // undefined means don't update
    };
  }
  // Reset when not applicable
  else {
    return {
      principalsList: [],
      selectedPrincipals: []
    };
  }
}

export function shouldClearPrincipalsOnBehalfOfChange(
  isAgentPrincipalUiEnabled,
  recipient,
  newOnBehalfOf,
  transactionType
) {
  if (!isAgentPrincipalUiEnabled) {
    return false;
  }

  if (!recipient?.[0]?.isMerged) {
    return false;
  }

  // OR logic: Always clear principals when selecting On Behalf Of
  if (shouldApplyOrLogic(recipient, transactionType)) {
    return newOnBehalfOf.length > 0;
  }

  return true;
}

export function getAvailableTransactionTypes(availableRoles) {
  return {
    hasPushStore: availableRoles?.hasContributorToConsumer || availableRoles?.hasPOB || availableRoles?.hasPOBOP,
    hasPushToAgent: availableRoles?.hasContributorToAgent
  };
}

export function filterParticipantsByTransactionType(participantList, transactionType) {
  if (!transactionType) return participantList;
  
  return participantList.filter(p => {
    if (p.id === 'MY_PITSTOP') {
      return transactionType === TransactionTypes.PUSH_STORE;
    }
    
    if (transactionType === TransactionTypes.PUSH_TO_AGENT) {
      return p.isAgent; // Only agents
    } else {
      return !p.isAgent; // Only non-agents
    }
  });
}

export function shouldApplyOrLogic(recipient, transactionType) {
  // if not of merged type or not push store, do not apply OR logic
  if (!recipient?.[0]?.isMerged || transactionType !== TransactionTypes.PUSH_STORE) {
    return false;
  }
  
  // OR logic only valid when merged type is POB_POBOP or CONTRIBUTOR_POB_POBOP
  const mergedType = recipient[0].mergedType;
  return mergedType === CombinedFlows.POB_POBOP || mergedType === CombinedFlows.CONTRIBUTOR_POB_POBOP;
}

export function shouldClearOnBehalfOfOnPrincipalsChange(
  isAgentPrincipalUiEnabled,
  recipient,
  newPrincipals,
  transactionType
) {
  if (!isAgentPrincipalUiEnabled) {
    return false;
  }

  // OR logic: Clear On Behalf Of when selecting My Principals
  if (shouldApplyOrLogic(recipient, transactionType)) {
    return newPrincipals.length > 0;
  }

  return false;
}
